/**
 * PBKDF2 Key Derivation (Web Crypto API)
 * Fallback for environments where WebAssembly/Argon2 is unavailable
 *
 * Security Parameters:
 * - Algorithm: PBKDF2-HMAC-SHA256
 * - Iterations: 100,000 (OWASP recommended minimum)
 * - Salt: 16 bytes (128 bits)
 * - Output: 32 bytes (256 bits) for AES-256-GCM
 *
 * Use Cases:
 * - WebView environments (limited WebAssembly support)
 * - Outlook add-in compatibility
 * - Fallback when Argon2id fails to load
 */

const PBKDF2_ITERATIONS = 100000;
const SALT_LENGTH = 16; // 128 bits
const KEY_LENGTH = 32; // 256 bits (for AES-256-GCM)

/**
 * Derives encryption key from password using PBKDF2
 *
 * @param {string} password - User password
 * @param {Uint8Array} salt - Random salt (16 bytes)
 * @returns {Promise<CryptoKey>} - Derived AES-GCM key
 */
export async function deriveKeyFromPassword(password, salt) {
  try {
    // Import password as raw key material
    const encoder = new TextEncoder();
    const passwordBuffer = encoder.encode(password);

    const passwordKey = await window.crypto.subtle.importKey(
      "raw",
      passwordBuffer,
      { name: "PBKDF2" },
      false,
      ["deriveBits", "deriveKey"]
    );

    // Derive AES-GCM key using PBKDF2
    const derivedKey = await window.crypto.subtle.deriveKey(
      {
        name: "PBKDF2",
        salt: salt,
        iterations: PBKDF2_ITERATIONS,
        hash: "SHA-256",
      },
      passwordKey,
      { name: "AES-GCM", length: 256 },
      true, // extractable (for encryption envelope storage)
      ["encrypt", "decrypt"]
    );

    return derivedKey;
  } catch (error) {
    console.error("PBKDF2 key derivation failed:", error);
    throw new Error("Failed to derive key from password");
  }
}

/**
 * Generates random salt for PBKDF2
 *
 * @returns {Uint8Array} - 16-byte random salt
 */
export function generateSalt() {
  return window.crypto.getRandomValues(new Uint8Array(SALT_LENGTH));
}

/**
 * Encrypts user's private key with password-derived key
 *
 * Flow:
 * 1. Generate random salt
 * 2. Derive AES-GCM key from password + salt (PBKDF2)
 * 3. Generate random nonce (IV)
 * 4. Encrypt private key with AES-GCM
 * 5. Return encrypted key + envelope (salt, nonce, algorithm)
 *
 * @param {string} privateKeyPem - RSA private key (PEM format)
 * @param {string} password - User password (Benutzer-Schlüssel)
 * @returns {Promise<{encryptedPrivateKey: string, envelope: object}>}
 */
export async function encryptPrivateKeyWithPassword(privateKeyPem, password) {
  try {
    // Step 1: Generate random salt
    const salt = generateSalt();

    // Step 2: Derive AES-GCM key from password
    const aesKey = await deriveKeyFromPassword(password, salt);

    // Step 3: Generate random nonce (12 bytes for AES-GCM)
    const nonce = window.crypto.getRandomValues(new Uint8Array(12));

    // Step 4: Encrypt private key
    const encoder = new TextEncoder();
    const privateKeyBuffer = encoder.encode(privateKeyPem);

    const encryptedBuffer = await window.crypto.subtle.encrypt(
      {
        name: "AES-GCM",
        iv: nonce,
      },
      aesKey,
      privateKeyBuffer
    );

    // Step 5: Create envelope with metadata
    const envelope = {
      kdf: {
        algorithm: "PBKDF2-HMAC-SHA256",
        salt: arrayBufferToBase64(salt),
        iterations: PBKDF2_ITERATIONS,
      },
      encryption: {
        algorithm: "AES-256-GCM",
        nonce: arrayBufferToBase64(nonce),
      },
      version: 1,
    };

    return {
      encryptedPrivateKey: arrayBufferToBase64(new Uint8Array(encryptedBuffer)),
      envelope: envelope,
    };
  } catch (error) {
    console.error("Private key encryption failed:", error);
    throw new Error("Failed to encrypt private key");
  }
}

/**
 * Decrypts user's private key with password
 *
 * Flow:
 * 1. Extract salt and nonce from envelope
 * 2. Derive AES-GCM key from password + salt
 * 3. Decrypt private key with AES-GCM
 * 4. Return decrypted PEM string
 *
 * @param {string} encryptedPrivateKeyBase64 - Base64 encrypted private key
 * @param {object} envelope - PrivateKeyEnvelope metadata
 * @param {string} password - User password (Benutzer-Schlüssel)
 * @returns {Promise<string>} - Decrypted RSA private key (PEM format)
 */
export async function decryptPrivateKeyWithPassword(
  encryptedPrivateKeyBase64,
  envelope,
  password
) {
  try {
    // Step 1: Extract parameters from envelope
    const salt = base64ToArrayBuffer(envelope.kdf.salt);
    const nonce = base64ToArrayBuffer(envelope.encryption.nonce);

    // Step 2: Derive AES-GCM key
    const aesKey = await deriveKeyFromPassword(password, salt);

    // Step 3: Decrypt private key
    const encryptedBuffer = base64ToArrayBuffer(encryptedPrivateKeyBase64);

    const decryptedBuffer = await window.crypto.subtle.decrypt(
      {
        name: "AES-GCM",
        iv: nonce,
      },
      aesKey,
      encryptedBuffer
    );

    // Step 4: Convert to string
    const decoder = new TextDecoder();
    return decoder.decode(decryptedBuffer);
  } catch (error) {
    console.error("Private key decryption failed:", error);
    throw new Error("Incorrect password or corrupted key");
  }
}

/**
 * Utility: ArrayBuffer to Base64
 */
function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}

/**
 * Utility: Base64 to ArrayBuffer
 */
function base64ToArrayBuffer(base64) {
  const binary = window.atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}
